'use client';

import { useState, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useEditableDotCMSPage, DotCMSLayoutBody } from '@dotcms/react';
import { DeviceTypeContext, getDeviceTypeFromWindow } from '@/app/utils/getDeviceType';
// TODO: Re-enable experiments after migration is complete
// import { useRouter } from 'next/navigation';
// import { withExperiments } from '@dotcms/experiments';

import { CaseStudyCarrousel } from './home/caseStudyCarrousel/caseStudyCarousel';
import { ImportantThings } from './home/importantThings';
import { CallToActionButton } from './shared/CallToActionButton';
import YouTube from './block-editor/youtube';
import IntegrationCard from '@/components/integrations/IntegrationCard';
import ScrollableGrid from './shared/ScrollableGrid';

// Content type components are code-split so a page only downloads the ones it renders.
const WebPageContent = dynamic(() => import('./content-types/webPageContent'));
const Banner = dynamic(() => import('./content-types/banner'));
const ImageComponent = dynamic(() => import('./content-types/image'));
const CaseStudy = dynamic(() =>
    import('./content-types/caseStudy').then((mod) => ({ default: mod.CaseStudy }))
);
const Collection = dynamic(() =>
    import('./content-types/collection-type/collection').then((mod) => ({
        default: mod.Collection
    }))
);
const MosaicCollection = dynamic(() =>
    import('./content-types/collection-type/mosaicCollection').then((mod) => ({
        default: mod.MosaicCollection
    }))
);
const Brands = dynamic(() => import('./content-types/brands'));
const ContactUs = dynamic(() => import('./content-types/contactUs'));
const ReusableTitle = dynamic(() => import('./content-types/reusableTitle'));
const PromoBanner = dynamic(() => import('./content-types/promoBanner'));
const DynamicBanner = dynamic(() => import('./content-types/dynamicBanner'));
const Reviews = dynamic(() => import('./content-types/reviews'));
const Announcement = dynamic(() => import('./content-types/announcement'));
const SuperParagraph = dynamic(() => import('./content-types/superParagraph'));
const ResourceLibrary = dynamic(() => import('./content-types/resourceLibrary'));
const PartnersListing = dynamic(() =>
    import('./content-types/parternsListing').then((mod) => ({ default: mod.PartnersListing }))
);
const IFrameCode = dynamic(() => import('./content-types/iFrameCode'));
const Video = dynamic(() => import('./content-types/video'));
const VideoCarrousel = dynamic(() => import('./content-types/videoCarrousel'));
const HubspotForm = dynamic(() =>
    import('./content-types/HubspotForm').then((mod) => ({ default: mod.HubspotForm }))
);
const BookTime = dynamic(() => import('./content-types/bookTime'));
const Playground = dynamic(() =>
    import('./content-types/playground').then((mod) => ({ default: mod.Playground }))
);
const LinkToogle = dynamic(() =>
    import('./content-types/linkToogle').then((mod) => ({ default: mod.LinkToogle }))
);
const PieChart = dynamic(() =>
    import('./content-types/PieChart').then((mod) => ({ default: mod.PieChart }))
);
const Chart = dynamic(() =>
    import('./content-types/Chart').then((mod) => ({ default: mod.Chart }))
);
const EnterpriseCard = dynamic(() => import('./content-types/enterpriseCard'));
const IndustryStats = dynamic(() => import('./content-types/industryStats'));
const IndustriesDisplay = dynamic(() => import('./content-types/industriesDisplay'));
const AwardCard = dynamic(() => import('./content-types/awardCard'));
const PressCard = dynamic(() => import('./content-types/pressCard'));
const HowItWorksSection = dynamic(() => import('./content-types/HowItWorksSection'));
const SplitIndex = dynamic(() => import('./content-types/splitIndex'));
const QuoteCard = dynamic(() => import('./content-types/quoteCard'));
const NumberedSteps = dynamic(() => import('./content-types/numberedSteps'));
const FeatureSplit = dynamic(() => import('./content-types/featureSplit'));
const FaqSection = dynamic(() => import('./content-types/faqSection'));
const GovernanceGrid = dynamic(() => import('./content-types/governanceGrid'));
const LinkCards = dynamic(() => import('./content-types/LinkCards'));
const DotCard = dynamic(() => import('./content-types/dotCard'));
const Accordion = dynamic(() => import('./content-types/accordion'));
const RelatedBlogs = dynamic(() => import('./content-types/relatedBlogs'));
const RelatedCaseStudies = dynamic(() => import('./content-types/relatedCaseStudies'));
const FeatureTable = dynamic(() => import('./content-types/featureTable'));
const BannerImages = dynamic(() => import('./content-types/bannerImages'));
const FeatureGrid = dynamic(() =>
    import('./content-types/FeatureGrid').then((mod) => ({ default: mod.FeatureGrid }))
);
const PreFooterBanner = dynamic(() => import('./content-types/preFooterBanner'));
const PersonaSection = dynamic(() => import('./content-types/personaSection'));
const PricingTable = dynamic(() => import('./content-types/pricingTable'));

// TODO: Re-enable experiments after migration is complete
// const experimentConfig = {
//     apiKey: process.env.NEXT_PUBLIC_EXPERIMENTS_API_KEY,
//     server: process.env.NEXT_PUBLIC_DOTCMS_HOST,
//     debug: process.env.NEXT_PUBLIC_EXPERIMENTS_DEBUG
// };

function ScrollableGridContentlet(contentlet) {
    const rawSlides = contentlet.slides || [];
    const slides = rawSlides.map((s) => ({
        label: s.label,
        title: s.headline,
        body: s.body,
        image: {
            src: s.image?.fileAsset?.idPath || s.image?.idPath,
            alt: s.title || ''
        },
        ctas: (s.callToAction || s.ctas || []).map((c) => ({
            url: c.url,
            urlName: c.urlName,
            buttonType: c.buttonType,
            ariaLabel: c.ariaLabel,
            position: c.position,
            iconPosition: c.iconPosition,
            extraSpace: c.extraSpace
        }))
    }));
    return (
        <ScrollableGrid
            type={contentlet.type1 || 'free'}
            eyebrow={contentlet.eyebrow}
            heading={contentlet.heading}
            slides={slides}
        />
    );
}

function IntegrationListing(contentlet) {
    const integrations = contentlet.widgetCodeJSON.results;
    return (
        <div className="container mx-auto grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {integrations.map((integration) => (
                <IntegrationCard key={integration.identifier} integration={integration} />
            ))}
        </div>
    );
}

// Stable module-level map — no deviceType closure so component references never change.
// deviceType is read by consuming components (Collection, MosaicCollection, RelatedBlogs)
// via DeviceTypeContext, which avoids unmount/remount when the value updates after hydration.
const buildComponentsMap = (content) => ({
    webPageContent: WebPageContent,
    Banner,
    Image: ImageComponent,
    CallToAction: CallToActionButton,
    FeatureSection: DotCard,
    Animation: BannerImages,
    LogoListing: Brands,
    Heading: ReusableTitle,
    CaseStudyCarousel: CaseStudyCarrousel,
    FeatureAccordion: Accordion,
    FeatureGrid,
    Importantthings: ImportantThings,
    Collection: (props) => <Collection {...props} content={content} />,
    MosaicCollection,
    CaseStudy,
    ContactUs,
    FeatureTable,
    VideoCarrousel,
    Video,
    Podcast: Video,
    ResourceLibrary,
    RelatedBlogs: (props) => <RelatedBlogs {...props} content={content} />,
    RelatedCaseStudies,
    Images: (props) => <ImageComponent {...props} dataComponent="images" />,
    PartnerListing: PartnersListing,
    Playground,
    HubspotForm,
    BookTime,
    PreFooterBanner,
    DocumentationLinks: LinkCards,
    DynamicBanner,
    PromoBanner,
    Reviews,
    IFrameCode,
    Announcement,
    SuperParagraph,
    LinkToogle,
    PieChart,
    Chart,
    Youtube: YouTube,
    EnterpriseCards: EnterpriseCard,
    FeaturesForUsersShowcase: PersonaSection,
    IntegrationListing,
    IndustryStatsDisplay: IndustryStats,
    IndustriesHomepage: IndustriesDisplay,
    PricingTable,
    AwardCard,
    PressCard,
    HowItWorksSection,
    ScrollableGrid: ScrollableGridContentlet,
    SplitIndex,
    QuoteCard,
    NumberedSteps,
    FeatureSplit,
    FaqSection,
    GovernanceGrid
});

export function MyPage({ pageResponse, content }) {
    // The hook returns undefined until UVE resolves a draft/non-live page, so read defensively.
    const pageAsset = useEditableDotCMSPage(pageResponse)?.pageAsset;
    const [deviceType, setDeviceType] = useState('desktop');
    useEffect(() => {
        setDeviceType(getDeviceTypeFromWindow());
    }, []);

    const components = useMemo(() => buildComponentsMap(content), [content]);

    return (
        <DeviceTypeContext.Provider value={deviceType}>
            <main>
                <DotCMSLayoutBody
                    page={pageAsset}
                    components={components}
                    mode={process.env.NEXT_PUBLIC_DOTCMS_MODE || 'production'}
                />
            </main>
        </DeviceTypeContext.Provider>
    );
}
